/*
  Belmint gifts
  1. <bm-personalizer>: engraving form. Shows the right fields per design, live review box,
     blocks add to cart until the required fields and the spelling checkbox are done.
  2. <bm-gift-addons>: gift wrap and message card. Added to the cart as their own lines
     before the main item, removed again if the main item fails to add.
  3. Order note sync: copies every engraving field and gift message into the cart note
     between markers, so it reaches the warehouse even if a tool ignores line item properties.
*/
(() => {
  if (window.BelmintGifts) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const MAX_FILE = 10 * 1024 * 1024;
  const FILE_OK = /\.(jpe?g|png|pdf)$/i;
  const NOTE_START = '--- Order details (do not edit) ---';
  const NOTE_END = '--- End of order details ---';

  // Which fields each engraving design uses. Layout notes are always offered.
  const DESIGN_FIELDS = {
    1: ['wording', 'extra'],
    2: ['initial', 'wording'],
    3: ['wording', 'extra'],
    4: ['initial', 'wording', 'extra'],
    5: ['wording', 'extra'],
    6: ['wording', 'extra'],
    7: ['wording'],
    8: ['wording', 'extra'],
    9: ['custom', 'artwork'],
  };

  const hasPubSub = () => typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  /* ---------- Engraving form ---------- */
  class BmPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.mode = this.dataset.mode;
      this.design = this.querySelector('[data-bm-design]');
      this.confirmBox = this.querySelector('[data-bm-confirm]');
      this.errorEl = this.querySelector('[data-bm-error]');
      this.reviewBody = this.querySelector('[data-bm-review-body]');
      this.utensilIds = (this.dataset.utensilVariants || '').split(',').filter(Boolean);
      this.offIds = (this.dataset.noEngraveVariants || '').split(',').filter(Boolean);
      this.utensilsOn = false;
      this.off = false;

      this.addEventListener('input', () => this.refresh());
      this.addEventListener('change', (e) => {
        if (e.target.type === 'file') {
          const problem = e.target.files[0] ? this.fileProblem(e.target.files[0]) : '';
          if (problem) {
            e.target.value = '';
            this.showError(problem);
          }
        }
        this.refresh();
      });

      const form = this.closest('form');
      if (form) form.addEventListener('change', () => setTimeout(() => this.syncVariant(), 0));
      if (hasPubSub()) {
        this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => this.syncVariant(), 0));
      }
      this.syncVariant();
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
    }

    // Utensil initial and the "No engraving" option both depend on the selected variant.
    syncVariant() {
      const form = this.closest('form');
      const idInput = form && form.querySelector('input[name="id"]');
      const id = idInput ? String(idInput.value) : '';
      this.utensilsOn = this.utensilIds.includes(id);
      this.off = this.offIds.includes(id);
      this.refresh();
    }

    activeKeys() {
      let keys = ['wording', 'extra'];
      if (this.mode === 'designs') {
        const opt = this.design && this.design.selectedOptions[0];
        const d = opt && opt.dataset.design;
        if (d && DESIGN_FIELDS[d]) keys = DESIGN_FIELDS[d].slice();
      }
      keys.push('notes');
      if (this.utensilsOn) keys.push('utensil');
      return keys;
    }

    field(key) {
      return this.querySelector(`[data-bm-field="${key}"]`);
    }

    value(key) {
      const wrap = this.field(key);
      if (!wrap || wrap.hidden) return '';
      const el = wrap.querySelector('input:not([type="file"]), textarea');
      return el ? el.value.trim() : '';
    }

    fileInput() {
      const wrap = this.field('artwork');
      return wrap ? wrap.querySelector('input[type="file"]') : null;
    }

    fileProblem(file) {
      if (!FILE_OK.test(file.name)) return 'Artwork must be a JPG, PNG or PDF file.';
      if (file.size > MAX_FILE) return 'Artwork must be 10 MB or smaller.';
      return '';
    }

    refresh() {
      if (this.off) {
        this.style.display = 'none';
        this.querySelectorAll('input, textarea, select').forEach((el) => {
          el.disabled = true;
        });
        return;
      }
      this.style.display = '';
      if (this.design) this.design.disabled = false;
      if (this.confirmBox) this.confirmBox.disabled = false;
      const keys = this.activeKeys();
      this.querySelectorAll('[data-bm-field]').forEach((wrap) => {
        const on = keys.includes(wrap.dataset.bmField);
        wrap.hidden = !on;
        wrap.querySelectorAll('input, textarea, select').forEach((el) => {
          el.disabled = !on;
        });
      });
      this.renderReview(keys);
      if (this.errorEl && !this.errorEl.hidden && !this.validate()) this.showError('');
    }

    renderReview(keys) {
      if (!this.reviewBody) return;
      const wine = this.mode === 'wine';
      const labels = {
        initial: 'Initial',
        wording: wine ? 'Line 1' : 'Wording',
        extra: wine ? 'Line 2' : 'Extra line',
        custom: 'Custom wording',
        utensil: 'Utensil initial',
      };
      const rows = [];
      Object.keys(labels).forEach((k) => {
        if (!keys.includes(k)) return;
        const v = this.value(k);
        if (v) rows.push([labels[k], v]);
      });
      const f = this.fileInput();
      if (f && !f.disabled && f.files && f.files[0]) rows.push(['Artwork', f.files[0].name]);

      this.reviewBody.textContent = '';
      if (!rows.length) {
        this.reviewBody.textContent = 'Enter your wording above to review it here.';
        return;
      }
      const dl = document.createElement('dl');
      if (this.design && this.design.value) rows.unshift(['Design', this.design.value]);
      rows.forEach(([k, v]) => {
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = k;
        dd.textContent = v;
        dl.append(dt, dd);
      });
      this.reviewBody.append(dl);
    }

    validate() {
      if (this.off) return '';
      const keys = this.activeKeys();
      if (this.mode === 'designs' && this.design && !this.design.value) return 'Please choose an engraving design.';
      if (keys.includes('custom')) {
        const f = this.fileInput();
        const file = f && f.files && f.files[0];
        if (!this.value('custom') && !file) return 'Please enter your custom wording or upload your artwork.';
        if (file) {
          const problem = this.fileProblem(file);
          if (problem) return problem;
        }
      }
      for (const k of keys) {
        const wrap = this.field(k);
        if (wrap && wrap.hasAttribute('data-bm-required') && !this.value(k)) {
          return k === 'initial' ? 'Please enter the monogram initial.' : 'Please enter the wording to engrave.';
        }
      }
      if (keys.includes('utensil') && !this.value('utensil')) return 'Please enter the initial for the utensils.';
      if (this.confirmBox && !this.confirmBox.checked) return 'Please tick the box to confirm you checked the spelling.';
      return '';
    }

    // Leave blank optional fields off the order. Dawn reads the form right after this,
    // then refresh() turns the fields back on.
    pruneEmpty() {
      this.querySelectorAll('input[type="text"], textarea').forEach((el) => {
        if (!el.disabled && !el.value.trim()) el.disabled = true;
      });
      setTimeout(() => this.refresh(), 1500);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
      if (message) this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* ---------- Gift add-ons ---------- */
  class BmGiftAddons extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.addEventListener('change', () => this.refresh());
      this.refresh();
    }

    checked() {
      return [...this.querySelectorAll('[data-bm-addon]:checked')];
    }

    message() {
      const t = this.querySelector('[data-bm-gift-message]');
      return t ? t.value.trim() : '';
    }

    refresh() {
      const card = this.querySelector('[data-bm-addon="card"]');
      const panel = this.querySelector('[data-bm-card-panel]');
      if (panel) panel.hidden = !(card && card.checked);
      // Buy it now skips the add-ons, so hide it while any add-on is ticked.
      const form = this.closest('form');
      const pay = form && form.querySelector('.shopify-payment-button');
      if (pay) pay.hidden = this.checked().length > 0;
    }
  }

  if (!customElements.get('bm-personalizer')) customElements.define('bm-personalizer', BmPersonalizer);
  if (!customElements.get('bm-gift-addons')) customElements.define('bm-gift-addons', BmGiftAddons);

  /* ---------- Submit handling ---------- */
  let inflight = null;

  const stop = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  const jsonFetch = (url, body) =>
    fetch(ROOT + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Bm-Gifts': '1' },
      body: JSON.stringify(body),
    }).then((r) => r.json().then((data) => ({ ok: r.ok && !data.status, data })));

  function finish(failed) {
    if (!inflight) return;
    const { form, temps, added } = inflight;
    inflight = null;
    temps.forEach((el) => el.remove());
    form.classList.remove('bm-busy');
    if (failed && added.length) {
      // Main item did not go in, so take the add-ons back out.
      fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const updates = {};
          added.forEach(({ key, qty }) => {
            const line = cart.items.find((i) => i.key === key);
            if (line) updates[key] = Math.max(0, line.quantity - qty);
          });
          if (Object.keys(updates).length) return jsonFetch('cart/update.js', { updates });
        })
        .then(() => scheduleNote())
        .catch(() => {});
    }
  }

  if (hasPubSub()) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
      if (inflight && e && e.source === 'product-form') finish(false);
    });
    subscribe(PUB_SUB_EVENTS.cartError, (e) => {
      if (inflight && e && e.source === 'product-form') finish(true);
    });
  }

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('product-form')) return;
      if (form.dataset.bmPass === '1') {
        delete form.dataset.bmPass;
        return; // second pass: let Dawn add the main item
      }
      const pz = form.querySelector('bm-personalizer');
      const addons = form.querySelector('bm-gift-addons');
      if (!pz && !addons) return;

      if (pz) {
        const problem = pz.validate();
        if (problem) {
          stop(event);
          pz.showError(problem);
          return;
        }
        pz.showError('');
        pz.pruneEmpty();
      }

      const chosen = addons ? addons.checked() : [];
      if (!chosen.length) return;

      stop(event);
      if (inflight) return;

      const msg = addons.message();
      const wantsCard = chosen.some((c) => c.dataset.bmAddon === 'card');
      if (wantsCard && !msg) {
        alertInline(addons, 'Please write the message for your gift card.');
        return;
      }
      alertInline(addons, '');

      const qty = Math.max(1, parseInt(new FormData(form).get('quantity'), 10) || 1);
      const title = addons.dataset.productTitle || '';
      const items = chosen.map((c) => {
        const properties = { For: title };
        if (c.dataset.bmAddon === 'card') properties['Gift message'] = msg;
        return { id: Number(c.dataset.variantId), quantity: c.dataset.bmAddon === 'wrap' ? qty : 1, properties };
      });

      form.classList.add('bm-busy');
      let res;
      try {
        res = await jsonFetch('cart/add.js', { items });
      } catch (err) {
        res = { ok: false, data: {} };
      }
      if (!res.ok) {
        form.classList.remove('bm-busy');
        alertInline(addons, (res.data && (res.data.description || res.data.message)) || 'Gift options could not be added. Please try again.');
        return;
      }

      const added = (res.data.items || []).map((line) => {
        const asked = items.find((i) => i.id === line.variant_id || i.id === line.id);
        return { key: line.key, qty: asked ? asked.quantity : line.quantity };
      });

      const temps = [];
      const hidden = (name, value) => {
        const el = document.createElement('input');
        el.type = 'hidden';
        el.name = `properties[${name}]`;
        el.value = value;
        form.appendChild(el);
        temps.push(el);
      };
      hidden('Gift add-ons', chosen.map((c) => c.dataset.name).join(', '));
      if (msg) hidden('Gift message', msg);

      inflight = { form, temps, added };
      setTimeout(() => {
        if (inflight && inflight.form === form) finish(false);
      }, 15000);

      if (pz) pz.pruneEmpty();
      form.dataset.bmPass = '1';
      const button = form.querySelector('[type="submit"][name="add"]');
      if (form.requestSubmit) form.requestSubmit(button || undefined);
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    true
  );

  function alertInline(addons, message) {
    let el = addons.querySelector('.bm-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'bm-error';
      el.setAttribute('role', 'alert');
      addons.append(el);
    }
    el.textContent = message;
    el.hidden = !message;
  }

  /* ---------- Order note sync ---------- */
  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const BLOCK_RE = new RegExp(`\\s*${escapeRe(NOTE_START)}[\\s\\S]*?${escapeRe(NOTE_END)}\\s*`, 'g');
  let noteTimer = null;

  async function syncNote() {
    try {
      const cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const lines = [];
      (cart.items || []).forEach((item) => {
        const props = item.properties || {};
        const keys = Object.keys(props).filter((k) => k && k.charAt(0) !== '_' && props[k] !== '' && props[k] != null);
        if (!keys.length) return;
        lines.push(`${item.sku || item.product_title} x${item.quantity}: ${keys.map((k) => `${k}: ${props[k]}`).join(' | ')}`);
      });
      const current = cart.note || '';
      const own = current.replace(BLOCK_RE, '\n').trim();
      const block = lines.length ? `${NOTE_START}\n${lines.join('\n')}\n${NOTE_END}` : '';
      const next = [own, block].filter(Boolean).join('\n\n');
      if (next !== current) await jsonFetch('cart/update.js', { note: next });
    } catch (err) {
      /* the line item properties still carry everything */
    }
  }

  const scheduleNote = () => {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(syncNote, 600);
  };

  // Re-sync after any cart change made by the theme (add, quantity change, removal, note edit).
  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const headers = (init && init.headers) || {};
    const ours = headers['X-Bm-Gifts'] === '1' || (headers.get && headers.get('X-Bm-Gifts') === '1');
    const p = nativeFetch(input, init);
    if (!ours && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      p.then(() => scheduleNote()).catch(() => {});
    }
    return p;
  };

  // Keep the order details block out of the note box the shopper sees.
  function cleanNoteBoxes() {
    document.querySelectorAll('textarea[name="note"]').forEach((t) => {
      if (t.value.indexOf(NOTE_START) !== -1) t.value = t.value.replace(BLOCK_RE, '\n').trim();
    });
  }
  let cleanQueued = false;
  const queueClean = () => {
    if (cleanQueued) return;
    cleanQueued = true;
    requestAnimationFrame(() => {
      cleanQueued = false;
      cleanNoteBoxes();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cleanNoteBoxes);
  else cleanNoteBoxes();
  new MutationObserver(queueClean).observe(document.documentElement, { childList: true, subtree: true });

  window.BelmintGifts = { syncNote };
})();
